/**
 * Axios client for ZTNA admin endpoints (mounted at /api/v1/admin/ztna/*).
 *
 * Reuses the global `api` axios instance from `@/api/client` so the auth
 * Bearer token + user/tenant headers + 401 redirect interceptor are
 * applied consistently with the rest of the app.
 *
 * Note: the global `api` instance has baseURL='/api', so endpoint paths
 * here drop the leading '/api' prefix and start at '/v1/...'.
 */
import { api } from '@/api/client'
import type {
  AccessStats,
  AdoptionPreview,
  AdoptResult,
  AppAccessMode,
  AppAccessModeResponse,
  AppAuditEventsResponse,
  AppDeprovisionRequest,
  AppHealthSummary,
  AppToggleResponse,
  AppUpdateRequest,
  AppUpdateResponse,
  CertExpiryItem,
  DashboardKpis,
  DiscardResult,
  DraftResponse,
  DnsLookupResponse,
  DriftAlertsResponse,
  HostedZoneItem,
  HostedZoneRecordItem,
  JobListResponse,
  JobPayload,
  JobResponse,
  JobSubmitResponse,
  OnboardingHistoryItem,
  PreflightResponse,
  ProbeResponse,
  TenantDiscoveryItem,
  TenantPrepareResponse,
} from './types'
import type { DnsMigrationState } from './Wizard/wizardState'

const PREFIX = '/v1/admin/ztna'

export const ztnaApi = {
  // Dashboard
  getKpis: async (): Promise<DashboardKpis> => {
    const r = await api.get<DashboardKpis>(`${PREFIX}/dashboard/kpis`)
    return r.data
  },
  listApps: async (
    options: { includeDisabled?: boolean } = {},
  ): Promise<AppHealthSummary[]> => {
    const params: Record<string, string> = {}
    if (options.includeDisabled) params.include_disabled = 'true'
    const r = await api.get<AppHealthSummary[]>(
      `${PREFIX}/dashboard/apps`,
      { params },
    )
    return r.data
  },
  getAccessStats: async (period: '24h' | '7d' = '24h'): Promise<AccessStats> => {
    const r = await api.get<AccessStats>(
      `${PREFIX}/dashboard/access-stats`,
      { params: { period } },
    )
    return r.data
  },
  getCertExpiry: async (days = 90): Promise<CertExpiryItem[]> => {
    const r = await api.get<CertExpiryItem[]>(
      `${PREFIX}/dashboard/cert-expiry`,
      { params: { days } },
    )
    return r.data
  },
  getOnboardingHistory: async (limit = 10): Promise<OnboardingHistoryItem[]> => {
    const r = await api.get<OnboardingHistoryItem[]>(
      `${PREFIX}/dashboard/onboarding-history`,
      { params: { limit } },
    )
    return r.data
  },

  // Probes + jobs
  probeApp: async (appId: number): Promise<ProbeResponse> => {
    const r = await api.post<ProbeResponse>(`${PREFIX}/apps/${appId}/probe`)
    return r.data
  },
  getJob: async (jobId: number): Promise<JobResponse> => {
    const r = await api.get<JobResponse>(`${PREFIX}/jobs/${jobId}`)
    return r.data
  },
  listJobs: async (status?: string): Promise<JobListResponse> => {
    const r = await api.get<JobListResponse>(`${PREFIX}/jobs`, {
      params: status ? { status } : undefined,
    })
    return r.data
  },
  cancelJob: async (id: number): Promise<JobResponse> => {
    const r = await api.post<JobResponse>(`${PREFIX}/jobs/${id}/cancel`)
    return r.data
  },
  retryJob: async (id: number): Promise<JobSubmitResponse> => {
    const r = await api.post<JobSubmitResponse>(`${PREFIX}/jobs/${id}/retry`)
    return r.data
  },
  validateJob: async (id: number): Promise<JobResponse> => {
    const r = await api.post<JobResponse>(`${PREFIX}/jobs/${id}/validate`)
    return r.data
  },

  // Wizard endpoints (for Lote 7) — typed now to keep the contract central.
  getActiveDraft: async (): Promise<DraftResponse | null> => {
    const r = await api.get<DraftResponse | null>(`${PREFIX}/drafts/active`)
    return r.data
  },
  createDraft: async (
    body: { tenant_id?: number | null; step_data?: Record<string, unknown> } = {},
  ): Promise<DraftResponse> => {
    const r = await api.post<DraftResponse>(`${PREFIX}/drafts`, {
      tenant_id: body.tenant_id ?? null,
      step_data: body.step_data ?? {},
    })
    return r.data
  },
  saveStep: async (
    draftId: number,
    stepN: number,
    stepData: Record<string, unknown>,
  ): Promise<DraftResponse> => {
    const r = await api.put<DraftResponse>(
      `${PREFIX}/drafts/${draftId}/step/${stepN}`,
      { step_data: stepData },
    )
    return r.data
  },
  deleteDraft: async (id: number): Promise<void> => {
    await api.delete(`${PREFIX}/drafts/${id}`)
  },
  submitOnboard: async (
    payload: JobPayload,
    draftId?: number | null,
    dnsMigration?: DnsMigrationState,
  ): Promise<JobSubmitResponse> => {
    const body: {
      draft_id: number | null
      payload: JobPayload
      dns_migration?: DnsMigrationState
    } = {
      draft_id: draftId ?? null,
      payload,
    }
    if (dnsMigration) body.dns_migration = dnsMigration
    const r = await api.post<JobSubmitResponse>(`${PREFIX}/onboard`, body)
    return r.data
  },
  preflight: async (payload: JobPayload): Promise<PreflightResponse> => {
    const r = await api.post<PreflightResponse>(
      `${PREFIX}/onboard/preflight`,
      payload,
    )
    return r.data
  },

  // Discovery
  listTenants: async (): Promise<TenantDiscoveryItem[]> => {
    const r = await api.get<TenantDiscoveryItem[]>(`${PREFIX}/tenants`)
    return r.data
  },
  /**
   * Sub-projeto 4 Lote 1 — bootstrap one-click do ConsoleAIZtnaProvisionRole
   * na conta tenant. Timeout 35s (assume role + create role tipicamente <30s).
   */
  prepareTenant: async (
    tenantId: number,
  ): Promise<TenantPrepareResponse> => {
    const r = await api.post<TenantPrepareResponse>(
      `${PREFIX}/tenants/${tenantId}/prepare`,
      {},
      { timeout: 35000 },
    )
    return r.data
  },
  listZones: async (tenantId: number): Promise<HostedZoneItem[]> => {
    const r = await api.get<HostedZoneItem[]>(`${PREFIX}/route53/zones`, {
      params: { tenant_id: tenantId },
    })
    return r.data
  },
  listRecords: async (
    tenantId: number,
    zoneId: string,
  ): Promise<HostedZoneRecordItem[]> => {
    const r = await api.get<HostedZoneRecordItem[]>(
      `${PREFIX}/route53/records`,
      { params: { tenant_id: tenantId, zone_id: zoneId } },
    )
    return r.data
  },
  dnsLookup: async (
    tenantId: number,
    zoneId: string,
    hostname: string,
  ): Promise<DnsLookupResponse> => {
    const r = await api.get<DnsLookupResponse>(
      `${PREFIX}/route53/dns-lookup`,
      { params: { tenant_id: tenantId, zone_id: zoneId, hostname } },
    )
    return r.data
  },

  /**
   * Audit timeline cursor-paginated (Lote 8 — Sub-projeto 3).
   * Filtra audit.events por resource_type='application' + resource_id=appId.
   */
  listAppAuditEvents: async (
    appId: number,
    params?: { limit?: number; before?: string },
  ): Promise<AppAuditEventsResponse> => {
    const r = await api.get<AppAuditEventsResponse>(
      `${PREFIX}/apps/${appId}/audit-events`,
      { params: { limit: params?.limit, before: params?.before } },
    )
    return r.data
  },

  /**
   * Toggle pause/resume de uma app ZTNA (Lote F1 — Sub-projeto 5).
   * Endpoint idempotente: toggle pra mesmo estado retorna previous=current.
   */
  toggleApp: async (
    appId: number,
    enabled: boolean,
  ): Promise<AppToggleResponse> => {
    const r = await api.post<AppToggleResponse>(
      `${PREFIX}/apps/${appId}/toggle`,
      { enabled },
    )
    return r.data
  },

  /**
   * O que a adocao faria com esta app — sem fazer.
   * Endpoint: GET /admin/ztna/apps/{id}/adoption-preview
   *
   * Custa duas chamadas AWS mais uma sonda: chamar SOB DEMANDA, quando o
   * operador abre a modal. Para saber se a app e' adotavel, a listagem ja traz
   * `tem_historico` e `discarded_at`.
   */
  adoptionPreview: async (appId: number): Promise<AdoptionPreview> => {
    const r = await api.get<AdoptionPreview>(`${PREFIX}/apps/${appId}/adoption-preview`)
    return r.data
  },

  /**
   * Adota a app, produzindo o historico que os verbos de ciclo de vida exigem.
   * Endpoint: POST /admin/ztna/apps/{id}/adopt
   *
   * `hostname` e' a confirmacao digitada — o backend recusa com
   * `hostname_mismatch` se nao bater, e recalcula o estado por conta propria.
   */
  adoptApp: async (appId: number, hostname: string): Promise<AdoptResult> => {
    const r = await api.post<AdoptResult>(`${PREFIX}/apps/${appId}/adopt`, { hostname })
    return r.data
  },

  /**
   * Tira a app do catalogo. ZERO chamada a AWS.
   * Endpoint: POST /admin/ztna/apps/{id}/discard
   */
  discardApp: async (appId: number, hostname: string): Promise<DiscardResult> => {
    const r = await api.post<DiscardResult>(`${PREFIX}/apps/${appId}/discard`, { hostname })
    return r.data
  },

  /**
   * Altera o modo de controle de acesso de uma app ZTNA.
   * Endpoint: POST /admin/ztna/apps/{id}/access-mode
   * mode: 'enforced' | 'paused' | 'open'
   */
  setAccessMode: async (
    appId: number,
    mode: AppAccessMode,
  ): Promise<AppAccessModeResponse> => {
    const r = await api.post<AppAccessModeResponse>(
      `${PREFIX}/apps/${appId}/access-mode`,
      { mode },
    )
    return r.data
  },

  /**
   * Edit parcial de campos seguros de app ZTNA (Lote F2 — Sub-projeto 5).
   * Whitelist 5 campos; backend valida upstream circular + URIs + access_policy.
   */
  updateApp: async (
    appId: number,
    patch: AppUpdateRequest,
  ): Promise<AppUpdateResponse> => {
    const r = await api.patch<AppUpdateResponse>(
      `${PREFIX}/apps/${appId}`,
      patch,
    )
    return r.data
  },

  /**
   * Deprovision de app ZTNA (Lote F3 — Sub-projeto 5).
   * Inicia job async; retorna job_id pra polling no JobBanner.
   * Idempotente: 2º click retorna mesmo job_id se mesmo mode em curso.
   */
  deprovisionApp: async (
    appId: number,
    payload: AppDeprovisionRequest,
  ): Promise<JobSubmitResponse> => {
    const r = await api.post<JobSubmitResponse>(
      `${PREFIX}/apps/${appId}/deprovision`,
      payload,
    )
    return r.data
  },

  /**
   * Lista alertas de drift ZTNA (Gap 4 — 2026-05-10).
   * status: 'open' (default) | 'resolved' | 'all'
   * limit: 1-200 (default 50)
   */
  listDriftAlerts: async (
    status: 'open' | 'resolved' | 'all' = 'open',
    limit = 50,
  ): Promise<DriftAlertsResponse> => {
    const r = await api.get<DriftAlertsResponse>(
      `${PREFIX}/drift-alerts`,
      { params: { status, limit } },
    )
    return r.data
  },
}

export type ZtnaApi = typeof ztnaApi
